import { useState, useEffect, lazy, Suspense } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Core components - load immediately
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import CartDrawer from "@/components/CartDrawer";
import NeonLikeButton from "@/components/NeonLikeButton";

// Lazy-loaded components - load on demand
const MessageBoardSection = lazy(() => import("@/components/MessageBoardSection"));
const RosterSection = lazy(() => import("@/components/RosterSection"));
const CoachesSection = lazy(() => import("@/components/CoachesSection"));
const SpecialGuestsSection = lazy(() => import("@/components/SpecialGuestsSection"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const MediaSection = lazy(() => import("@/components/MediaSection"));
const CommentsSection = lazy(() => import("@/components/CommentsSection"));
const MerchSection = lazy(() => import("@/components/MerchSection"));
const ScheduleSection = lazy(() => import("@/components/ScheduleSection"));
const SponsorsSection = lazy(() => import("@/components/SponsorsSection"));
const ReferralSection = lazy(() => import("@/components/ReferralSection"));
const AboutSection = lazy(() => import("@/components/AboutSection"));
const WeatherTracker = lazy(() => import("@/components/WeatherTracker"));
const GrindLeaderboard = lazy(() => import("@/components/GrindLeaderboard"));
const PalmBeachHSFootball = lazy(() => import("@/components/PalmBeachHSFootball"));
const CryptoPaymentModal = lazy(() => import("@/components/CryptoPaymentModal"));
const DonationButton = lazy(() => import("@/components/DonationButton"));

// Heavy AI/Feature components - lazy load with priority
const FormAnalyzer = lazy(() => import("@/components/FormAnalyzer"));
const HighlightReelMaker = lazy(() => import("@/components/HighlightReelMaker"));
const TradingCardGenerator = lazy(() => import("@/components/TradingCardGenerator"));
const NFLCombineChallenge = lazy(() => import("@/components/NFLCombineChallenge"));
const GrindGallery = lazy(() => import("@/components/GrindGallery"));

// Loading fallback component
const SectionLoader = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 className="w-8 h-8 text-neon-blue animate-spin" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to fix image URLs - prepend backend URL if relative path
const fixImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Fix image URLs in array of objects
const fixImageUrls = (items, imageFields = ['image_url']) => {
  return items.map(item => {
    const fixed = { ...item };
    imageFields.forEach(field => {
      if (fixed[field]) {
        fixed[field] = fixImageUrl(fixed[field]);
      }
    });
    return fixed;
  });
};

const HomePage = () => {
  const { user, token, authAxios } = useAuth();
  const [searchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [plans, setPlans] = useState([]);
  const [media, setMedia] = useState([]);
  const [comments, setComments] = useState([]);
  const [merchandise, setMerchandise] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  
  // Crypto payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    seedAndFetchData();
    
    // Check for referral code in URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      // Show auth modal to encourage sign up
      if (!token) {
        setTimeout(() => {
          toast.info(`Use code ${refCode.toUpperCase()} for 15% off!`);
          setShowAuthModal(true);
        }, 1500);
      }
    }

    // Auto-refresh data every 30 seconds to keep in sync across devices
    const refreshInterval = setInterval(() => {
      fetchAllData();
    }, 30000);

    // Also refresh when window gains focus (user switches back to tab)
    const handleFocus = () => {
      fetchAllData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const seedAndFetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`);
      // Then fetch all data
      fetchAllData();
    } catch (e) {
      console.error("Error seeding data:", e);
      fetchAllData();
    }
  };

  const fetchAllData = async () => {
    try {
      // Add timestamp to prevent browser caching
      const ts = Date.now();
      const [playersRes, coachesRes, sponsorsRes, announcementsRes, plansRes, mediaRes, commentsRes, merchRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/players?_t=${ts}`),
        axios.get(`${API}/coaches?_t=${ts}`),
        axios.get(`${API}/sponsors?_t=${ts}`),
        axios.get(`${API}/announcements?_t=${ts}`),
        axios.get(`${API}/pricing?_t=${ts}`),
        axios.get(`${API}/media?_t=${ts}`),
        axios.get(`${API}/comments?_t=${ts}`),
        axios.get(`${API}/merchandise?_t=${ts}`),
        axios.get(`${API}/schedule?_t=${ts}`)
      ]);
      // Fix image URLs for all data with images
      setPlayers(fixImageUrls(playersRes.data, ['image_url']));
      setCoaches(fixImageUrls(coachesRes.data, ['image_url']));
      setSponsors(fixImageUrls(sponsorsRes.data, ['logo_url']));
      setAnnouncements(announcementsRes.data);
      setPlans(plansRes.data);
      setMedia(fixImageUrls(mediaRes.data, ['url', 'thumbnail_url']));
      setComments(commentsRes.data);
      setMerchandise(fixImageUrls(merchRes.data, ['image_url']));
      setSchedules(schedulesRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
    }
  };

  const addToCart = (item, size, color, quantity = 1) => {
    const cartItem = {
      ...item,
      cartId: `${item.id}-${size}-${color}-${Date.now()}`,
      selectedSize: size,
      selectedColor: color,
      quantity
    };
    setCart([...cart, cartItem]);
    toast.success(`${item.name} added to cart!`);
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  // State for merch payment
  const [showMerchPaymentModal, setShowMerchPaymentModal] = useState(false);
  const [merchCheckoutLoading, setMerchCheckoutLoading] = useState(false);

  const handleCheckout = async () => {
    if (!token) {
      setShowAuthModal(true);
      toast.error("Please login to checkout");
      return;
    }
    
    // Close cart and show payment modal
    setShowCart(false);
    setShowMerchPaymentModal(true);
  };

  const handleMerchPaymentSelect = async (paymentMethod) => {
    setMerchCheckoutLoading(true);
    try {
      const merchItems = cart.map(item => ({
        id: item.id,
        quantity: item.quantity,
        size: item.selectedSize,
        color: item.selectedColor
      }));

      const response = await authAxios.post('/checkout/merch', {
        merch_items: merchItems,
        origin_url: window.location.origin,
        payment_method: paymentMethod
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setMerchCheckoutLoading(false);
    }
  };

  const handlePlanCheckout = async (planId) => {
    if (!token) {
      setShowAuthModal(true);
      toast.error("Please login to register");
      return;
    }

    // Find the selected plan
    const plan = plans.find(p => p.id === planId);
    if (plan) {
      setSelectedPlanForPayment(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentMethodSelect = async (paymentMethod) => {
    if (!selectedPlanForPayment) return;
    
    setCheckoutLoading(true);
    try {
      const response = await authAxios.post('/checkout/plan', {
        plan_id: selectedPlanForPayment.id,
        origin_url: window.location.origin,
        payment_method: paymentMethod
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const handleAddComment = async (content) => {
    if (!token) {
      setShowAuthModal(true);
      toast.error("Please login to comment");
      return;
    }

    try {
      const response = await authAxios.post('/comments', { content });
      setComments([response.data, ...comments]);
      toast.success("Comment posted!");
    } catch (e) {
      toast.error("Failed to post comment");
    }
  };

  const handleToggleAttendance = async (scheduleId, userName) => {
    if (!token) {
      setShowAuthModal(true);
      toast.error("Please login to RSVP");
      return;
    }

    try {
      const response = await authAxios.post(`/schedule/${scheduleId}/attend`, { user_name: userName });
      setSchedules(schedules.map(s => s.id === scheduleId ? response.data : s));
      toast.success("Attendance updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update attendance");
    }
  };

  return (
    <div className="min-h-screen bg-void-black relative overflow-x-hidden">
      {/* Grid Background */}
      <div className="fixed inset-0 grid-bg pointer-events-none opacity-50" />
      
      <Navigation 
        cartCount={cart.length} 
        onCartClick={() => setShowCart(true)}
        onAuthClick={() => setShowAuthModal(true)}
      />
      
      <main>
        <HeroSection />
        
        {/* Neon Like Button - Facebook Follow */}
        <div className="relative py-8 flex justify-center">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neon-blue/5 to-transparent" />
          <NeonLikeButton facebookUrl="https://www.facebook.com/GTSRsports/" />
        </div>
        
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <MessageBoardSection announcements={announcements} />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <RosterSection players={players} />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <CoachesSection coaches={coaches} />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <SpecialGuestsSection />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <PricingSection plans={plans} onCheckout={handlePlanCheckout} />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <MediaSection media={media} />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <GrindGallery />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <FormAnalyzer />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <HighlightReelMaker />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <TradingCardGenerator />
        </Suspense>
        <div className="section-divider" />
        
        {/* Weather & Schedule Section */}
        <section id="weather-schedule" className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Weather Tracker */}
              <div className="lg:col-span-1">
                <div className="sticky top-4">
                  <div className="text-center mb-6">
                    <span className="font-orbitron text-xs tracking-[0.2em] text-neon-blue uppercase">Live Conditions</span>
                    <h3 className="font-graffiti text-2xl neon-text-pink mt-2">WEATHER</h3>
                  </div>
                  <Suspense fallback={<SectionLoader />}>
                    <WeatherTracker />
                  </Suspense>
                </div>
              </div>
              
              {/* Schedule */}
              <div className="lg:col-span-2">
                <Suspense fallback={<SectionLoader />}>
                  <ScheduleSection 
                    schedules={schedules} 
                    onToggleAttendance={handleToggleAttendance}
                    userName={user?.name || user?.email?.split('@')[0]}
                    embedded={true}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        </section>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <GrindLeaderboard />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <NFLCombineChallenge />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <PalmBeachHSFootball />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <MerchSection merchandise={merchandise} onAddToCart={addToCart} />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <SponsorsSection sponsors={sponsors} />
        </Suspense>
        <div className="section-divider" />
        
        {/* Donation Section */}
        <section id="donate" className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <span className="font-orbitron text-xs tracking-[0.2em] text-neon-pink uppercase">Support Our Athletes</span>
              <h2 className="font-graffiti text-3xl md:text-4xl neon-text-blue mt-2">HELP US GROW</h2>
              <p className="font-rajdhani text-gray-400 mt-3 max-w-xl mx-auto">
                Your donation helps provide elite training resources to young athletes in our community
              </p>
            </div>
            <Suspense fallback={<SectionLoader />}>
              <DonationButton />
            </Suspense>
          </div>
        </section>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <ReferralSection />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <CommentsSection 
            comments={comments} 
            onAddComment={handleAddComment}
            isLoggedIn={!!token}
          />
        </Suspense>
        <div className="section-divider" />
        
        <Suspense fallback={<SectionLoader />}>
          <AboutSection />
        </Suspense>
      </main>
      
      <Footer />

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        initialReferralCode={referralCode}
      />

      <CartDrawer 
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        cart={cart}
        onRemoveItem={removeFromCart}
        onCheckout={handleCheckout}
      />

      <Suspense fallback={null}>
        <CryptoPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlanForPayment(null);
            setCheckoutLoading(false);
          }}
          onSelectPayment={handlePaymentMethodSelect}
          planName={selectedPlanForPayment?.name}
          planPrice={selectedPlanForPayment?.price}
          isLoading={checkoutLoading}
        />

        <CryptoPaymentModal
          isOpen={showMerchPaymentModal}
          onClose={() => {
            setShowMerchPaymentModal(false);
            setMerchCheckoutLoading(false);
          }}
          onSelectPayment={handleMerchPaymentSelect}
          planName="Merchandise"
          planPrice={cart.reduce((sum, item) => sum + item.price * item.quantity, 0)}
          isLoading={merchCheckoutLoading}
        />
      </Suspense>
    </div>
  );
};

export default HomePage;
